import * as fontkit from 'fontkit';

const TARGET_LOCALES = ['ko', 'en'];

const LOCALE_ALIASES = {
	'en': 'en',
	'en-us': 'en',
	'en-gb': 'en',
	'ko': 'ko',
	'ko-kr': 'ko',
};

// 로케일별 기대 문자권 (없는 로케일은 검사하지 않음)
const SCRIPT_PATTERNS = {
	'ko': /[가-힣ᄀ-ᇿ㄰-㆏]/,    // 한글
};

function isValidName(value) {
	if (!value || typeof value !== 'string') return false;
	const trimmed = value.trim();
	if (trimmed === '') return false;
	if (trimmed.indexOf('�') !== -1) return false;
	return true;
}

function resolveTargetLocale(rawLocale) {
	const lower = String(rawLocale).toLowerCase().replace(/_/g, '-');
	if (LOCALE_ALIASES[lower]) return LOCALE_ALIASES[lower];
	const langOnly = lower.split('-')[0];
	if (LOCALE_ALIASES[langOnly]) return LOCALE_ALIASES[langOnly];
	return null;
}

function normalizeRecord(record) {
	if (!record) return {};
	if (typeof record === 'string') {
		return isValidName(record) ? { en: record.trim() } : {};
	}

	const result = {};
	for (const [rawLocale, value] of Object.entries(record)) {
		if (!isValidName(value)) continue;
		const target = resolveTargetLocale(rawLocale);
		if (target && !result[target]) {
			result[target] = value.trim();
		}
	}
	return result;
}

function appendSubfamily(familyName, subfamilyName, alwaysAppend) {
	if (!familyName) return null;
	if (!subfamilyName) return familyName;

	if (!alwaysAppend && /^regular$/i.test(subfamilyName)) {
		return familyName;
	}

	const escaped = subfamilyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	if (new RegExp(`\\b${escaped}\\s*$`, 'i').test(familyName)) {
		return familyName;
	}

	return `${familyName} ${subfamilyName}`.trim();
}

function pickByLocale(record, locale) {
	return (record && record[locale]) || null;
}

function getFilenameStem(fileName) {
	if (!fileName) return null;
	return String(fileName).replace(/\.(woff2?|ttf|otf|ttc|otc)$/i, '');
}

function valueMatchesLocaleScript(value, locale) {
	const pattern = SCRIPT_PATTERNS[locale];
	if (!pattern) return true;
	if (!value) return false;
	return pattern.test(value);
}

// 모든 후보 record(원본/정규화 모두)에서 해당 로케일의 스크립트를 포함하는 첫 값을 찾음
function findValueByScript(candidates, locale) {
	const pattern = SCRIPT_PATTERNS[locale];
	if (!pattern) return null;

	for (const record of candidates) {
		if (!record) continue;
		if (typeof record === 'string') {
			if (isValidName(record) && valueMatchesLocaleScript(record, locale)) {
				return record.trim();
			}
			continue;
		}
		for (const value of Object.values(record)) {
			if (typeof value === 'string' && isValidName(value) && valueMatchesLocaleScript(value, locale)) {
				return value.trim();
			}
		}
	}
	return null;
}

export function extractFontInfo(font, fileName) {
	const records = (font && font.name && font.name.records) || {};

	const preferredFamily    = normalizeRecord(records.preferredFamily);    // NameID 16
	const preferredSubfamily = normalizeRecord(records.preferredSubfamily); // NameID 17
	const fontFamily         = normalizeRecord(records.fontFamily);         // NameID 1
	const fontSubfamily      = normalizeRecord(records.fontSubfamily);      // NameID 2
	const wwsFamily          = normalizeRecord(records.wwsFamily);          // NameID 21
	const wwsSubfamily       = normalizeRecord(records.wwsSubfamily);       // NameID 22
	const fullName           = normalizeRecord(records.fullName);           // NameID 4

	const psName = isValidName(font && font.postscriptName) ? font.postscriptName : null;
	const psFallback = psName ? psName.split('-')[0].replace(/([a-z])([A-Z])/g, '$1 $2') : null;

	const localeInfo = {};

	for (const locale of TARGET_LOCALES) {
		let composed = null;

		const pf = pickByLocale(preferredFamily, locale);
		if (pf) {
			composed = appendSubfamily(pf, pickByLocale(preferredSubfamily, locale), true);
		}

		if (!composed) {
			const ff = pickByLocale(fontFamily, locale);
			if (ff) {
				composed = appendSubfamily(ff, pickByLocale(fontSubfamily, locale), true);
			}
		}

		if (!composed) {
			const wf = pickByLocale(wwsFamily, locale);
			if (wf) {
				composed = appendSubfamily(wf, pickByLocale(wwsSubfamily, locale), true);
			}
		}

		if (!composed) {
			composed = pickByLocale(fullName, locale);
		}

		if (composed) {
			localeInfo[locale] = composed;
		}
	}

	// 스크립트 기반 폴백:
	// 예) NameID 1.ko = "Cafe24 Ssurround Bold"(영문)이지만 NameID 16에 한글명 "카페24 써라운드"가 있는 케이스.
	// ko 값에 한글이 누락된 경우, NameID 16 → 1 → 21 → 4 순으로 한글 포함 값을 탐색하여 교체.
	const scriptFallbackCandidates = [
		records.preferredFamily,
		preferredFamily,
		records.fontFamily,
		fontFamily,
		records.wwsFamily,
		wwsFamily,
		records.fullName,
		fullName,
	];
	for (const locale of TARGET_LOCALES) {
		if (!SCRIPT_PATTERNS[locale]) continue;
		const current = localeInfo[locale];
		if (current && valueMatchesLocaleScript(current, locale)) continue;

		const scriptValue = findValueByScript(scriptFallbackCandidates, locale);
		if (scriptValue) {
			localeInfo[locale] = scriptValue;
		}
	}

	if (!localeInfo.en) {
		if (psFallback) {
			localeInfo.en = psFallback;
		} else {
			const anyValue = Object.values(localeInfo)[0];
			if (anyValue) {
				localeInfo.en = anyValue;
			} else {
				const stem = getFilenameStem(fileName);
				if (stem) localeInfo.en = stem;
			}
		}
	}

	const isVariable = !!(font && font.variationAxes && Object.keys(font.variationAxes).length > 0);

	return {
		localeInfo,
		postScriptName: psName,
		isVariable,
	};
}

export async function extractWebFontInfo(file) {
	const fileName = file && file.name;

	let parsed;
	try {
		const arrayBuffer = await file.arrayBuffer();
		const uint8Array = new Uint8Array(arrayBuffer);
		parsed = fontkit.create(uint8Array);
	} catch (error) {
		console.warn('Failed to parse font file:', error);
		const stem = getFilenameStem(fileName) || 'Unknown';
		return {
			localeInfo: { en: stem },
			postScriptName: null,
			isVariable: false,
			isCollection: false,
			error: error && error.message,
		};
	}

	const isCollection = parsed && parsed.type === 'TTC' && Array.isArray(parsed.fonts) && parsed.fonts.length > 0;
	const font = isCollection ? parsed.fonts[0] : parsed;

	const info = extractFontInfo(font, fileName);
	return { ...info, isCollection };
}
